<template>
    <v-card width="100%" flat class="border-grey-all">
        <v-card-title v-if="title" class="grey lighten-4">
            <span class="title customPrimary--text">{{title}}</span>
        </v-card-title>
        <v-data-table
            :headers="headers"
            :items="seats"
            :loading="loading"
            hide-actions
            >
            <template v-slot:items="props">
                <tr>
                    <td class="px-1 text-xs-center">{{ _categoryClassifier(props.item.category)['label'] }}</td>
                    <td class="px-1 text-xs-center">
                        <v-select
                        v-if="_isObjectUsable(discounts)"
                        v-model="props.item.ticket_type"
                        :items="Object.values(DEFINES.SEATS_TICKET_TYPES)"
                        item-text="label"
                        item-value="value"
                        single-line
                        class="py-1 my-0"
                        hide-details
                        ></v-select>
                        <span v-else>{{$t('screener.not_available.title')}}</span>
                    </td>
                    <td class="px-1 text-xs-center">{{ props.item.label }}</td>
                    <td class="px-1 text-xs-center">
                        <span v-if="currency">{{ getCurrencySymbol(currency) }}{{ _calculateSeatPrice(props.item) }}</span>
                        <span v-if="props.item.ticket_type">&nbsp;(-{{discounts[props.item.ticket_type]}}%)</span>
                    </td>
                </tr>
            </template>
            <template v-if="seats.length" slot="footer">
                <!-- Selected seats vs Max seats to be selected -->
                <tr>
                    <td colspan="2"><h3>{{$t('screener.seats.cart.total_seats.title')}}&nbsp;:&nbsp;</h3></td>
                    <td colspan="2"><h3>{{seats.length}}/{{maxSeatsToBeSelected}}</h3></td>
                </tr>
                <!-- Total actual amount without coupon -->
                <tr v-if="applied_coupon.applicable">
                    <td colspan="2"><h3>{{$t('screener.seats.cart.total_amount.title')}}&nbsp;:&nbsp;</h3></td>
                    <td colspan="2">
                        <h3>
                            <span v-if="currency">{{ getCurrencySymbol(currency) }}</span>
                            <span>{{price}}</span>
                        </h3>
                    </td>
                </tr>
                <!-- Coupon discounted amount with percentage -->
                <tr v-if="applied_coupon.applicable">
                    <td colspan="2"><h3>{{$t('screener.seats.cart.discount.title')}}&nbsp;:&nbsp;</h3></td>
                    <td colspan="2">
                        <h3>
                            <span v-if="currency">-{{ getCurrencySymbol(currency) }}</span>
                            <span>{{applied_coupon.available_discount.toFixed(2)}}</span>
                        </h3>
                    </td>
                </tr>
                <!-- Total to pay with/without -->
                <tr>
                    <td colspan="2"><h3>{{$t('screener.seats.cart.total_to_pay.title')}}&nbsp;:&nbsp;</h3></td>
                    <td colspan="2">
                        <h3 v-if="!applied_coupon.checking">
                            <span v-if="currency">{{ getCurrencySymbol(currency) }}</span>
                            <span>{{ totalPrice }}</span>
                        </h3>
                         <v-progress-circular
                            v-else
                            indeterminate
                         ></v-progress-circular>
                    </td>
                </tr>
                <tr>
                    <!-- Coupon codes -->
                    <td colspan="4" class="pt-3">
                        <CouponForm
                         ref="couponFormComp"
                        :having_coupon.sync="having_coupon"
                        :coupon_code.sync="coupon_code"
                        @redeem="redeemCoupon"
                        ></CouponForm>
                    </td>
                </tr>
            </template>
            <template v-slot:no-data>
                <v-layout wrap justify-center text-xs-center class="pa-3">
                    <div class="pa-2 title greyDarken1--text">
                       {{$t('screener.seats.cart.empty.title')}}
                    </div>
                </v-layout>
            </template>
        </v-data-table>
        <v-divider v-if="instructions.length"></v-divider>
        <!-- Instructions -->
        <v-card-text v-if="instructions.length">
            <div class="greyDarken1--text">
                <ul>
                    <li v-for="instruction,index in instructions" :key="index" v-html="instruction.text"></li>
                </ul>
            </div>
        </v-card-text>
        <v-divider></v-divider>
        <!-- Consumer Email -->
        <v-card-text v-if="seats.length">
            <v-form ref="consumer">
            <v-text-field
                outline
                :rules="[rules.min_length(3)]"
                v-if="seats.length"
                :label="$t('screener.seats.book.enter_consumer_name')"
                v-model="copyConsumer.name"
            />
            <v-text-field
                outline
                :rules="[rules.required, rules.email]"
                v-if="seats.length"
                :label="$t('screener.seats.book.enter_consumer_email')"
                v-model="copyConsumer.email"
            />
          </v-form>
          </v-card-text>
        <v-divider ></v-divider>
        <!-- Cart actions -->
        <v-card-actions class="pa-3">
            <v-spacer></v-spacer>
             <template>
                <v-btn outline color='customPrimary' :disabled="!seats.length" @click.stop="bookSeats()">
                    <v-icon small class="pr-1">mdi-checkbox-marked-circle</v-icon>{{$t('screener.seats.btn.bookseats')}}
                </v-btn>
            </template>
        </v-card-actions>
    </v-card>
</template>

<script>
import {seatCategoryMethods} from "@/mixins/seats";
import { getCurrencySymbol } from "@/helper";
import CouponForm from "@/components/CouponForm"
import { mapGetters } from "vuex";

import rules from "@/validation";
export default {
    name:"SeatsCart",
    props:{
        title:{
            type:String,
            default:null
        },
        seats:{
            type:Array,
            default:[]
        },
        currency:{
            type: String,
            default:null
        },
        headers:{
            type:Array,
            default:[]
        },
        actions:{
            type:Array,
            default:function () {
                return [];
            },
        },
        instructions:{
            type:Array,
            default:function () {
                return [];
            },
        },
        discounts:{
            type: Object,
            default:{}
        },
        loading:{
            type:Boolean,
            default:false
        },
        consumer:{
            type:Object,
            default:null
        },
        maxSeatsToBeSelected:{
            type:Number|String,
            default:0
        },
        screenerId:{
            type:Number,
            default:0
        }
    },
    mixins:[seatCategoryMethods],
    data() {
        return {
            rules,
            copyConsumer:{
                name:null,
                email:null
            },
            ticketDiscounts:{},
            having_coupon:false,
            coupon_code:'',
            applied_coupon: {
                applicable: false,
                available_discount: 0,
                discount_percentage: 0,
                checking:false,
            },
        }
    },
    components:{
        CouponForm
    },
    computed:{
        ...mapGetters({
            selected_company_id: "company/getSelected",
         }),
         price(){
            var price = 0;
            if(this.seats.length){
                price =  this.seats.reduce((total, seat) => total + parseFloat(this._calculateSeatPrice(seat)), 0);
                this.redeemCoupon();
            }
            return ( price < 0 ? 0 : price );
        },
        totalPrice(){
            let total = this.price;
            if(this.applied_coupon.applicable){
                total = this.price - (this.price < this.applied_coupon.available_discount ? this.price :  this.applied_coupon.available_discount)
            }
            return total.toFixed(2);
        }
    },
    watch:{
        coupon_code(){
            this.$emit("update:coupon_code",this.coupon_code)
        },
        copyConsumer: {
            handler: function() {
                this.$emit("update:consumer", this.copyConsumer);
            },
            deep:true
        },
        consumer: {
            handler: function(newVal, oldVal) {
                this.copyConsumer = newVal;
            },
            deep:true
        },
        seats: {
            handler: function(newVal, oldVal) {
                if(newVal.length == 0){
                    this.having_coupon= false;
                }
            },
            deep:true
        },
        having_coupon:{
            handler:function(newVal,oldVal){
                if(newVal == false){
                    this.resetAppliedCoupon();
                }
                this.$emit("update:having_coupon",this.having_coupon)
            }
        }
    },
    methods: {
        resetAppliedCoupon(){
            this.applied_coupon = {
                applicable: false,
                available_discount: 0,
                discount_percentage: 0,
                checking:false,
            };
            this.coupon_code='';
            if(this.$refs.couponFormComp){
                this.$refs.couponFormComp.reset();
            }
        },
        _isObjectUsable(object){
            if(!object){
                return false;
            };
            var result = false;
            Object.values(object).every(val => result |= parseFloat(val));
            return result;
        },
        async bookSeats(){
            if (!this.$refs.consumer.validate()) {
                return;
            }
            await this.$emit("onBook");
        },
        getCurrencySymbol(currency) {
            return getCurrencySymbol(currency);
        },
        _calculateSeatPrice(seat){
            if(!seat.ticket_type || !this.discounts.hasOwnProperty(seat.ticket_type)){
                return seat.price;
            }
            let discount =  (seat.price * (this.discounts[seat.ticket_type]/100));
            let price = seat.price-discount;
            return price.toFixed(2);
        },
        async redeemCoupon(){
            if(!this.coupon_code){
                return;
            }
            if (!this.$refs.couponFormComp.validate()) {
                return false;
            }
            try {
                this.applied_coupon.checking = true;
                var response = await this.$store.dispatch("consumer/redeemCoupon", {
                    screener_id: this.screenerId,
                    seat_indexes:this.seats.map(seat => seat.id),
                    coupon_code: this.coupon_code,
                    company_id: this.selected_company_id,
                });
                this.applied_coupon.applicable = true;
                this.applied_coupon.available_discount = this.price * (response.discount / 100);
                if(this.applied_coupon.available_discount >  this.price){
                    this.applied_coupon.available_discount = this.price;
                }
                this.applied_coupon.discount_percentage = response.discount;
            } catch (error) {
                this.$store.dispatch("app/showMessage", {
                    message: error.message,
                    color: 'error',
                });
            }finally{
                 this.applied_coupon.checking = false;
            }
        }
    }
}
</script>

<style>

</style>
